import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { TaskBar } from '@components/TaskBar';
import { queryKeys } from '@constants/queryKeys.constant';
import { EStaleTime } from '@enums/staleTime.enum';
import { serviceRequestService } from '@services/serviceRequests/service';

import { ServiceRequestActivityTabs } from './@components/ActivityTabs';
import { ServiceRequestData } from './@components/Data';
import { AutomatedEmailsWarning } from './@components/Emails/AutomatedWarning';
import { ServiceRequestDetailsLoading } from './@components/Loading';
import { ServiceRequestRequester } from './@components/Requester';
import { ServiceRequestTabs } from './@components/Tabs';
import { ServiceRequestTimeline } from './@components/Timeline';

function serviceRequestQueryOptions(id: string) {
  return queryOptions({
    queryKey: queryKeys.serviceRequests.detail(id),
    queryFn: () => serviceRequestService.getById(id),
    staleTime: EStaleTime.OneHour,
  });
}

export const Route = createFileRoute(
  '/_layout/(private)/service-requests/$id/',
)({
  loader: ({ context, params }) => {
    const options = serviceRequestQueryOptions(params.id);
    return context.queryClient.ensureQueryData({
      ...options,
      revalidateIfStale: true,
    });
  },
  pendingComponent: ServiceRequestDetailsLoading,
  pendingMs: 300,
  pendingMinMs: 600,
  component: ServiceRequestDetails,
});

function ServiceRequestDetails() {
  const { id } = Route.useParams();
  const { data } = useSuspenseQuery(serviceRequestQueryOptions(id));

  return (
    <div className="grid py-4 lg:p-0">
      <AutomatedEmailsWarning
        automatedEmailsEnabled={data.serviceRequest.automaticEmailsEnabled}
      />
      <div className="border-secondary mb-6 grid gap-y-8 border-b-2 border-dotted pb-2 lg:grid-cols-2 lg:gap-4 2xl:grid-cols-3">
        <div className="relative grid grid-rows-[auto_1fr] gap-2">
          <ServiceRequestRequester
            serviceRequest={{
              id: data.serviceRequest.id,
              requester: data.serviceRequest.requester,
            }}
          />

          <ServiceRequestTabs
            counts={data.serviceRequest.counts}
            id={id}
            requesterId={data.serviceRequest.requester.id}
          />
        </div>

        <ServiceRequestData serviceRequest={data.serviceRequest} />

        <div className="mx-auto my-4 h-full w-auto max-w-xs space-y-6 overflow-clip sm:max-w-full md:p-2 lg:col-span-2 2xl:col-span-1 2xl:mx-0">
          <ServiceRequestTimeline />
          {data.serviceRequest.task && (
            <TaskBar
              additionalInformation={
                data.serviceRequest.task.additionalInformation
              }
              dueDate={data.serviceRequest.task.dueDate}
              id={data.serviceRequest.task.id}
              queryKeyToToggle={queryKeys.serviceRequests.detail(
                data.serviceRequest.id,
              )}
              serviceRequestId={data.serviceRequest.id}
              type={data.serviceRequest.task.type}
            />
          )}
        </div>
      </div>

      <ServiceRequestActivityTabs
        businessLineId={data.serviceRequest.businessLineId}
        unassignedInterventions={
          data.serviceRequest.counts?.unassignedInterventions ?? 0
        }
      />
    </div>
  );
}
